import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "react-toastify";
import {
  AlertTriangle,
  CloudUpload,
  Loader,
  Search,
  Sprout,
  Trash2,
  Wifi,
  WifiOff,
  X,
} from "lucide-react";
import {
  cleanupSyncedData,
  deleteOfflineFarmer,
  getAllOfflineFarmers,
  getUnsyncedFarmers,
  syncAllOfflineData,
} from "../local database/OfflineUtils";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.05, delayChildren: 0.08 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.3, ease: "easeOut" },
  },
  exit: { opacity: 0, y: 6, transition: { duration: 0.18 } },
};

const headerVariants = {
  hidden: { opacity: 0, y: -12 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: "easeOut" },
  },
};

const formatDate = (value) => {
  if (!value) return "Unknown date";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Unknown date";

  return date.toLocaleDateString();
};

const getFarmerKey = (farmer) => farmer?.id ?? farmer?.uuid;

const StatusPill = ({ synced }) => (
  <span
    className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide ring-1 ${
      synced
        ? "bg-green-50 text-emerald-700 ring-green-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:ring-emerald-800"
        : "bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:ring-amber-800"
    }`}
  >
    <span
      className={`h-1.5 w-1.5 rounded-full ${
        synced ? "bg-green-600" : "bg-amber-400 animate-pulse"
      }`}
    />
    {synced ? "Synced" : "Pending"}
  </span>
);

const StatCard = ({ value, label, accent }) => (
  <motion.div
    variants={itemVariants}
    whileHover={{ y: -2 }}
    transition={{ duration: 0.2 }}
    className="flex flex-col items-center justify-center min-w-0 px-2 py-3 bg-white shadow-sm rounded-xl ring-1 ring-gray-100 dark:bg-gray-800 dark:ring-gray-700 sm:px-5"
  >
    <span className={`text-xl font-bold tabular-nums sm:text-2xl ${accent}`}>
      {value}
    </span>
    <span className="mt-0.5 truncate text-[10px] font-medium uppercase tracking-wider text-gray-400 sm:text-[11px] sm:tracking-widest">
      {label}
    </span>
  </motion.div>
);

const DeleteButton = ({ farmer, disabled, deleting, onDelete }) => (
  <motion.button
    type="button"
    whileHover={disabled ? undefined : { scale: 1.04 }}
    whileTap={disabled ? undefined : { scale: 0.96 }}
    onClick={() => onDelete(farmer)}
    disabled={disabled}
    aria-label={`Delete ${farmer?.name || "farmer"}`}
    title="Delete farmer from this device"
    className="inline-flex items-center justify-center text-gray-500 transition-colors bg-gray-100 rounded-lg h-9 w-9 shrink-0 hover:bg-red-50 hover:text-red-500 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-red-900/20 dark:hover:text-red-400"
  >
    {deleting ? (
      <Loader className="w-4 h-4 animate-spin" />
    ) : (
      <Trash2 className="w-4 h-4" />
    )}
  </motion.button>
);

const DeleteDialog = ({ farmer, deleting, onCancel, onConfirm }) => {
  if (!farmer) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-end justify-center p-0 bg-gray-900/40 sm:items-center sm:p-4"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !deleting) onCancel();
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 24, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 20, scale: 0.98 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-farmer-title"
        className="w-full p-5 bg-white shadow-xl rounded-t-2xl ring-1 ring-gray-100 dark:bg-gray-900 dark:ring-gray-800 sm:max-w-md sm:rounded-2xl sm:p-6"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start min-w-0 gap-3">
            <div className="flex items-center justify-center w-10 h-10 text-red-500 shrink-0 rounded-xl bg-red-50 ring-1 ring-red-100 dark:bg-red-900/20 dark:text-red-400 dark:ring-red-900/40">
              <AlertTriangle className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <h3
                id="delete-farmer-title"
                className="text-base font-bold text-gray-800 dark:text-gray-100"
              >
                Delete offline farmer?
              </h3>
              <p className="mt-1 text-sm leading-5 text-gray-500 dark:text-gray-400">
                <span className="font-semibold text-gray-700 dark:text-gray-200">
                  {farmer.name || "This farmer"}
                </span>{" "}
                and the farmer&apos;s locally saved bundle data will be removed
                from this device.
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onCancel}
            disabled={deleting}
            aria-label="Close delete dialog"
            className="inline-flex items-center justify-center w-8 h-8 text-gray-400 transition-colors rounded-lg shrink-0 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50 dark:hover:bg-gray-800 dark:hover:text-gray-300"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {!farmer.synced && (
          <div className="mt-4 rounded-xl bg-amber-50 px-3.5 py-3 text-xs leading-5 text-amber-700 ring-1 ring-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:ring-amber-800">
            This farmer is still pending sync. Deleting now permanently removes
            the unsynced local copy.
          </div>
        )}

        <div className="mt-5 grid grid-cols-2 gap-2.5">
          <button
            type="button"
            onClick={onCancel}
            disabled={deleting}
            className="rounded-xl bg-gray-100 px-4 py-2.5 text-sm font-semibold text-gray-600 transition-colors hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={deleting}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-red-500 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-red-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {deleting ? (
              <Loader className="w-4 h-4 animate-spin" />
            ) : (
              <Trash2 className="w-4 h-4" />
            )}
            {deleting ? "Deleting…" : "Delete"}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

const OfflineFarmers = ({ onDataSynced }) => {
  const [offlineFarmers, setOfflineFarmers] = useState([]);
  const [unsyncedCount, setUnsyncedCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletingFarmerKey, setDeletingFarmerKey] = useState(null);
  const [isOnline, setIsOnline] = useState(() =>
    typeof navigator === "undefined" ? true : navigator.onLine,
  );

  const refreshLocalData = useCallback(async ({ showLoader = false } = {}) => {
    if (showLoader) setLoading(true);

    try {
      const [farmers, unsynced] = await Promise.all([
        getAllOfflineFarmers(),
        getUnsyncedFarmers(),
      ]);

      setOfflineFarmers(farmers);
      setUnsyncedCount(unsynced.length);
    } catch (error) {
      console.error("Error loading offline farmers:", error);
      toast.error("Failed to load offline farmers");
    } finally {
      if (showLoader) setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshLocalData({ showLoader: true });
  }, [refreshLocalData]);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
    };

    const handleOffline = () => {
      setIsOnline(false);
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  useEffect(() => {
    if (!deleteTarget || deletingFarmerKey != null) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") setDeleteTarget(null);
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [deleteTarget, deletingFarmerKey]);

  const syncedCount = useMemo(
    () => offlineFarmers.filter((farmer) => farmer.synced === true).length,
    [offlineFarmers],
  );

  const filteredFarmers = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return offlineFarmers;

    return offlineFarmers.filter((farmer) => {
      const searchable = [
        farmer?.id,
        farmer?.uuid,
        farmer?.name,
        farmer?.prefered_bundle,
      ]
        .filter((value) => value != null)
        .join(" ")
        .toLowerCase();

      return searchable.includes(query);
    });
  }, [offlineFarmers, searchTerm]);

  const handleSyncAll = async () => {
    if (!isOnline) {
      toast.error("Cannot sync while offline");
      return;
    }

    if (unsyncedCount === 0 || syncing) return;

    setSyncing(true);

    try {
      const result = await syncAllOfflineData();

      // Always refresh. A partial sync still changes local state.
      await refreshLocalData();

      if (result.success) {
        toast.success(`Successfully synced ${result.synced} farmer(s)`);
      } else {
        toast.warning(
          `Synced ${result.synced} farmer(s), ${result.errors} failed`,
        );
      }

      if (result.synced > 0 && onDataSynced) onDataSynced();
    } catch (error) {
      console.error("Sync error:", error);
      toast.error("Failed to sync data");
    } finally {
      setSyncing(false);
    }
  };

  const handleCleanupSynced = async () => {
    if (syncedCount === 0 || cleaning || syncing) return;

    setCleaning(true);

    try {
      const result = await cleanupSyncedData();
      await refreshLocalData();
      toast.success(
        `Removed ${result.deletedFarmers} synced farmer${
          result.deletedFarmers === 1 ? "" : "s"
        } from this device`,
      );
    } catch (error) {
      console.error("Cleanup error:", error);
      toast.error("Failed to cleanup synced data");
    } finally {
      setCleaning(false);
    }
  };

  const requestFarmerDelete = (farmer) => {
    if (syncing || cleaning) return;
    setDeleteTarget(farmer);
  };

  const confirmFarmerDelete = async () => {
    if (!deleteTarget || deletingFarmerKey != null) return;

    const farmerKey = getFarmerKey(deleteTarget);
    setDeletingFarmerKey(farmerKey);

    try {
      await deleteOfflineFarmer(deleteTarget);
      setDeleteTarget(null);
      await refreshLocalData();
      toast.success(
        `${deleteTarget.name || "Farmer"} deleted from this device`,
      );
    } catch (error) {
      console.error("Delete farmer error:", error);
      toast.error(error?.message || "Failed to delete farmer");
    } finally {
      setDeletingFarmerKey(null);
    }
  };

  const operationInProgress = syncing || cleaning || deletingFarmerKey != null;

  return (
    <motion.div
      className="w-full font-sans"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      <div className="w-full mx-auto mt-5 overflow-hidden bg-white shadow rounded-xl ring-1 ring-gray-100 dark:bg-gray-900 dark:ring-gray-800 md:container xl:max-w-7xl">
        <motion.div
          variants={headerVariants}
          className="px-4 pt-5 pb-5 border-b border-gray-100 dark:border-gray-800 sm:px-6 sm:pt-6"
        >
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex items-center min-w-0 gap-3">
              <div className="flex items-center justify-center w-10 h-10 shrink-0 rounded-xl bg-green-50 ring-1 ring-emerald-100 dark:bg-emerald-900/30 dark:ring-emerald-800">
                <Sprout className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
              </div>

              <div className="min-w-0">
                <h2 className="text-base font-bold tracking-tight text-gray-800 truncate dark:text-gray-100">
                  Offline Farmers
                </h2>
                <div className="mt-0.5 flex items-center gap-1.5">
                  <motion.span
                    animate={isOnline ? { scale: [1, 1.2, 1] } : undefined}
                    transition={{
                      repeat: Infinity,
                      duration: 2,
                      ease: "easeInOut",
                    }}
                    className={`h-2 w-2 rounded-full ${
                      isOnline ? "bg-emerald-400" : "bg-red-400"
                    }`}
                  />
                  <span
                    className={`text-xs font-medium ${
                      isOnline
                        ? "text-emerald-600 dark:text-emerald-400"
                        : "text-red-500"
                    }`}
                  >
                    {isOnline ? "Connected" : "Offline"}
                  </span>
                  {isOnline ? (
                    <Wifi className="h-3.5 w-3.5 text-green-600" />
                  ) : (
                    <WifiOff className="h-3.5 w-3.5 text-red-400" />
                  )}
                </div>
              </div>
            </div>

            <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:w-auto sm:items-center">
              <motion.button
                type="button"
                whileHover={
                  !isOnline || unsyncedCount === 0 ? undefined : { scale: 1.02 }
                }
                whileTap={
                  !isOnline || unsyncedCount === 0 ? undefined : { scale: 0.98 }
                }
                onClick={handleSyncAll}
                disabled={
                  !isOnline || syncing || cleaning || unsyncedCount === 0
                }
                className={`inline-flex min-h-10 items-center justify-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold transition-all sm:px-4 ${
                  !isOnline || unsyncedCount === 0
                    ? "cursor-not-allowed bg-gray-100 text-gray-400 dark:bg-gray-800"
                    : syncing
                      ? "cursor-not-allowed bg-green-50 text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400"
                      : "bg-green-600 text-white shadow-sm shadow-green-200 hover:bg-emerald-700 dark:shadow-none"
                }`}
              >
                {syncing ? (
                  <Loader className="w-4 h-4 animate-spin" />
                ) : (
                  <CloudUpload className="w-4 h-4" />
                )}
                <span>
                  {syncing
                    ? "Syncing…"
                    : `Sync All${unsyncedCount ? ` (${unsyncedCount})` : ""}`}
                </span>
              </motion.button>

              <motion.button
                type="button"
                whileHover={syncedCount === 0 ? undefined : { scale: 1.02 }}
                whileTap={syncedCount === 0 ? undefined : { scale: 0.98 }}
                onClick={handleCleanupSynced}
                disabled={syncedCount === 0 || cleaning || syncing}
                className="items-center justify-center hidden gap-2 px-3 py-2 text-sm font-semibold text-gray-500 transition-all bg-gray-100 min-h-10 rounded-xl hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700 sm:px-4"
              >
                {cleaning ? (
                  <Loader className="w-4 h-4 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
                <span>{cleaning ? "Cleaning…" : "Cleanup"}</span>
              </motion.button>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2 mt-5 sm:gap-3">
            <StatCard
              value={offlineFarmers.length}
              label="Total"
              accent="text-gray-700 dark:text-gray-200"
            />
            <StatCard
              value={syncedCount}
              label="Synced"
              accent="text-emerald-600"
            />
            <StatCard
              value={unsyncedCount}
              label="Pending"
              accent="text-amber-500"
            />
          </div>
        </motion.div>

        <div className="px-4 py-4 sm:px-6 sm:py-5">
          {!loading && offlineFarmers.length > 0 && (
            <div className="mb-4 flex flex-col gap-2.5 sm:flex-row sm:items-center sm:justify-between">
              <div className="relative w-full sm:max-w-sm">
                <Search className="absolute w-4 h-4 text-gray-400 -translate-y-1/2 pointer-events-none left-3 top-1/2" />
                <input
                  type="search"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder="Search farmer or package…"
                  aria-label="Search offline farmers"
                  className="w-full rounded-xl bg-gray-50 py-2.5 pl-9 pr-9 text-sm text-gray-700 outline-none ring-1 ring-gray-100 transition focus:ring-green-200 dark:bg-gray-800 dark:text-gray-200 dark:ring-gray-700 dark:focus:ring-emerald-800"
                />
                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => setSearchTerm("")}
                    aria-label="Clear search"
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded-md p-1 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-300"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>

              <p className="text-xs text-gray-400 dark:text-gray-500">
                {searchTerm
                  ? `${filteredFarmers.length} of ${offlineFarmers.length} shown`
                  : `${offlineFarmers.length} farmer${offlineFarmers.length === 1 ? "" : "s"} saved locally`}
              </p>
            </div>
          )}

          {loading ? (
            <div className="flex flex-col items-center justify-center gap-3 py-16">
              <Loader className="text-green-600 h-7 w-7 animate-spin" />
              <span className="text-sm font-medium text-gray-400">
                Loading farmers…
              </span>
            </div>
          ) : offlineFarmers.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex flex-col items-center justify-center gap-3 py-16 text-center"
            >
              <div className="flex items-center justify-center h-14 w-14 rounded-2xl bg-gray-50 ring-1 ring-gray-100 dark:bg-gray-800 dark:ring-gray-700">
                <Sprout className="text-gray-300 h-7 w-7 dark:text-gray-600" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-500 dark:text-gray-400">
                  No offline farmers found
                </p>
                <p className="mt-1 text-xs text-gray-300 dark:text-gray-600">
                  Farmers saved while offline will appear here.
                </p>
              </div>
            </motion.div>
          ) : filteredFarmers.length === 0 ? (
            <div className="px-4 py-10 text-center rounded-xl bg-gray-50 ring-1 ring-gray-100 dark:bg-gray-800/60 dark:ring-gray-700">
              <Search className="w-6 h-6 mx-auto text-gray-300 dark:text-gray-600" />
              <p className="mt-2 text-sm font-semibold text-gray-500 dark:text-gray-400">
                No matching farmer
              </p>
              <button
                type="button"
                onClick={() => setSearchTerm("")}
                className="mt-2 text-xs font-semibold text-emerald-600 hover:text-emerald-700 dark:text-emerald-400"
              >
                Clear search
              </button>
            </div>
          ) : (
            <>
              {/* Mobile: card layout avoids horizontal scrolling. */}
              <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className="space-y-3 md:hidden"
              >
                <AnimatePresence mode="popLayout">
                  {filteredFarmers.map((farmer) => {
                    const farmerKey = getFarmerKey(farmer);
                    const isDeleting = deletingFarmerKey === farmerKey;

                    return (
                      <motion.article
                        layout
                        key={farmerKey}
                        variants={itemVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        className="p-4 bg-white shadow-sm rounded-xl ring-1 ring-gray-100 dark:bg-gray-900 dark:ring-gray-800"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <h3 className="text-sm font-bold text-gray-800 truncate dark:text-gray-100">
                              {farmer.name || "Unnamed farmer"}
                            </h3>
                            <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-gray-400 dark:text-gray-500">
                              <span className="rounded-md bg-gray-100 px-1.5 py-0.5 font-mono font-semibold text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                                #{farmer.id ?? "—"}
                              </span>
                              <span>{formatDate(farmer.created_at)}</span>
                            </div>
                          </div>

                          <DeleteButton
                            farmer={farmer}
                            onDelete={requestFarmerDelete}
                            disabled={operationInProgress}
                            deleting={isDeleting}
                          />
                        </div>

                        <div className="flex items-end justify-between gap-3 pt-3 mt-4 border-t border-gray-100 dark:border-gray-800">
                          <div className="min-w-0">
                            <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500">
                              Package
                            </p>
                            {farmer.prefered_bundle ? (
                              <p className="mt-1 text-xs font-medium truncate text-emerald-700 dark:text-emerald-400">
                                {farmer.prefered_bundle}
                              </p>
                            ) : (
                              <p className="mt-1 text-xs italic text-gray-300 dark:text-gray-600">
                                No package
                              </p>
                            )}
                          </div>
                          <StatusPill synced={farmer.synced} />
                        </div>
                      </motion.article>
                    );
                  })}
                </AnimatePresence>
              </motion.div>

              {/* Desktop/tablet: denser table presentation. */}
              <div className="hidden w-full overflow-hidden rounded-xl ring-1 ring-gray-100 dark:ring-gray-800 md:block">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[760px] text-sm">
                    <thead>
                      <tr className="bg-gray-50 dark:bg-gray-800/60">
                        <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500">
                          ID
                        </th>
                        <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500">
                          Farmer
                        </th>
                        <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500">
                          Package
                        </th>
                        <th className="px-4 py-3 text-center text-[11px] font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500">
                          Status
                        </th>
                        <th className="px-4 py-3 text-center text-[11px] font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500">
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <AnimatePresence mode="popLayout">
                        {filteredFarmers.map((farmer) => {
                          const farmerKey = getFarmerKey(farmer);
                          const isDeleting = deletingFarmerKey === farmerKey;

                          return (
                            <motion.tr
                              layout
                              key={farmerKey}
                              variants={itemVariants}
                              initial="hidden"
                              animate="visible"
                              exit="exit"
                              className="transition-colors border-t group border-gray-50 hover:bg-gray-50/70 dark:border-gray-800 dark:hover:bg-gray-800/40"
                            >
                              <td className="px-4 py-3.5">
                                <span className="inline-flex rounded-md bg-gray-100 px-2 py-0.5 font-mono text-xs font-semibold text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                                  #{farmer.id ?? "—"}
                                </span>
                              </td>
                              <td className="px-4 py-3.5">
                                <p className="max-w-[260px] truncate text-sm font-semibold text-gray-800 dark:text-gray-200">
                                  {farmer.name || "Unnamed farmer"}
                                </p>
                                <p className="mt-1 text-[10px] text-gray-300 dark:text-gray-600">
                                  {formatDate(farmer.created_at)}
                                </p>
                              </td>
                              <td className="px-4 py-3.5">
                                <div className="max-w-xs truncate">
                                  {farmer.prefered_bundle ? (
                                    <span className="inline-flex max-w-full truncate rounded-lg bg-green-50 px-2.5 py-1 text-xs font-medium text-emerald-700 ring-1 ring-emerald-100 dark:bg-emerald-900/20 dark:text-emerald-400 dark:ring-emerald-800">
                                      {farmer.prefered_bundle}
                                    </span>
                                  ) : (
                                    <span className="text-xs italic text-gray-300 dark:text-gray-600">
                                      No package
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-3.5 text-center">
                                <StatusPill synced={farmer.synced} />
                              </td>
                              <td className="px-4 py-3.5 text-center">
                                <DeleteButton
                                  farmer={farmer}
                                  onDelete={requestFarmerDelete}
                                  disabled={operationInProgress}
                                  deleting={isDeleting}
                                />
                              </td>
                            </motion.tr>
                          );
                        })}
                      </AnimatePresence>
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <AnimatePresence>
        {deleteTarget && (
          <DeleteDialog
            farmer={deleteTarget}
            deleting={deletingFarmerKey != null}
            onCancel={() => setDeleteTarget(null)}
            onConfirm={confirmFarmerDelete}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default OfflineFarmers;
